import { DotMatrix } from '@clickguard/design-system'
import { Platform } from './types'
import GoogleIcon from '../../../assets/img/google.svg'
import MetaIcon from '../../../assets/img/meta.svg'
import MicrosoftIcon from '../../../assets/img/microsoft.svg'
import { InviteTeamMember } from './invite-team-member'

interface PlatformSelectionViewProps {
	onPlatformSelect: (platform: Platform) => void
	loading: boolean
	isOnboarding?: boolean
}

export const PlatformSelectionView = ({
	onPlatformSelect,
	loading,
	isOnboarding = true
}: PlatformSelectionViewProps) => {
	return (
		<div className="flex flex-col gap-4">
			{loading && (
				<div className="flex items-center justify-center gap-2 py-4">
					<DotMatrix size="sm" />
					<p className="text-[14px] leading-[18px] text-t-checkbox-unactive">
						Loading your credentials...
					</p>
				</div>
			)}
			{/* Platform Selection */}
			<div className="flex flex-col gap-4">
				<h2 className="text-[20px] leading-[26px] font-medium text-t-default">
					Select advertising platform
				</h2>
				<div className="grid md:grid-cols-2 grid-cols-1 gap-4">
					{/* Google Ads */}
					<button
						onClick={() => onPlatformSelect('GADS')}
						disabled={loading}
						className="flex items-center gap-4 p-6 border border-t-radio-gray rounded-[16px] hover:border-brand hover:bg-t-platform-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<img src={GoogleIcon} alt="Google Ads" className="w-10 h-10" />
						<span className="text-[18px] leading-[24px] font-medium text-t-google-ad">
							Google Ads
						</span>
					</button>

					{/* Meta Ads */}
					<button
						onClick={() => onPlatformSelect('FADS')}
						disabled={loading}
						className="flex items-center gap-4 p-6 border border-t-radio-gray rounded-[16px] hover:border-brand hover:bg-t-platform-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<img src={MetaIcon} alt="Meta Ads" className="w-10 h-10" />
						<span className="text-[18px] leading-[24px] font-medium text-t-google-ad">
							Meta Ads
						</span>
					</button>

					{/* Microsoft Ads */}
					<button
						onClick={() => onPlatformSelect('BADS')}
						disabled={loading}
						className="flex items-center gap-4 p-6 border border-t-radio-gray rounded-[16px] hover:border-brand hover:bg-t-platform-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<img
							src={MicrosoftIcon}
							alt="Microsoft Ads"
							className="w-10 h-10"
						/>
						<span className="text-[18px] leading-[24px] font-medium text-t-google-ad">
							Microsoft Ads
						</span>
					</button>

					{/* More to come */}
					<button
						disabled
						className="flex items-center gap-4 p-6 border border-t-radio-gray rounded-[16px] cursor-not-allowed"
					>
						<svg
							className="w-10 h-10 flex-shrink-0"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M6.06422 12.4175C6.06422 12.5196 6.1358 12.646 6.22297 12.6987L9.84051 14.88C9.92769 14.9327 10.0708 14.9327 10.158 14.88L13.7756 12.6987C13.8631 12.646 13.9343 12.5196 13.9343 12.4175V11.9086C13.9343 11.8066 13.8509 11.7232 13.7489 11.7232H11.8943C11.7923 11.7232 11.6372 11.7662 11.5497 11.8185L10.1576 12.6545C10.0701 12.7072 9.92729 12.7072 9.83975 12.6545L8.44774 11.8185C8.36019 11.7659 8.2886 11.6397 8.2886 11.5377V7.42095C8.2886 7.31895 8.3691 7.21287 8.46699 7.18503L9.82013 6.80264C9.91839 6.7748 10.079 6.7748 10.1769 6.80264L11.5297 7.18503C11.6279 7.21287 11.7915 7.23547 11.8935 7.23547H13.7481C13.8501 7.23547 13.9336 7.15204 13.9336 7.05003V5.82452C13.9336 5.72252 13.8534 5.61644 13.7551 5.58861L10.1769 4.5775C10.0786 4.54969 9.91803 4.54969 9.82013 4.5775L6.24188 5.58861C6.14359 5.61644 6.06348 5.72252 6.06348 5.82452V12.4175H6.06422Z"
								style={{ fill: 'var(--t-email-blue)' }}
							/>
							<path
								d="M9.74558 18.9369C9.88541 19.021 10.1139 19.021 10.2537 18.9369L17.2043 14.7455C17.3441 14.6613 17.4584 14.4588 17.4584 14.2956V8.67544C17.4584 8.51224 17.3249 8.37873 17.1616 8.37873H10.2964C10.1332 8.37873 9.99962 8.51224 9.99962 8.67544V10.3075C9.99962 10.4707 10.1332 10.6042 10.2964 10.6042H14.9365C15.0998 10.6042 15.2333 10.7377 15.2333 10.9009V13.0389C15.2333 13.2021 15.119 13.4046 14.9792 13.4888L10.2541 16.3382C10.1142 16.4224 9.88578 16.4224 9.74594 16.3382L5.02047 13.4888C4.88064 13.4046 4.76639 13.2021 4.76639 13.0389V4.95331C4.76639 4.79011 4.89473 4.62023 5.052 4.57572L9.7144 3.2586C9.87131 3.21409 10.1283 3.21409 10.2856 3.2586L14.948 4.57572C15.1049 4.62023 15.2336 4.79011 15.2336 4.95331V6.93845C15.2336 7.10165 15.3672 7.2352 15.5304 7.2352H17.1624C17.3256 7.2352 17.4591 7.10165 17.4591 6.93845V3.35096C17.4591 3.18775 17.3308 3.01787 17.1735 2.97374L10.2853 1.0331C10.1283 0.988965 9.87131 0.988965 9.71403 1.0331L2.82613 2.97374C2.66886 3.01787 2.54053 3.18775 2.54053 3.35096V14.2959C2.54053 14.4591 2.65477 14.6617 2.7946 14.7459L9.74518 18.9372L9.74558 18.9369Z"
								style={{ fill: 'var(--t-email-blue)' }}
							/>
						</svg>
						<span className="text-[18px] leading-[24px] font-medium text-t-google-ad">
							More to come
						</span>
					</button>
				</div>
			</div>

			{isOnboarding && <InviteTeamMember step="ad_account" />}
		</div>
	)
}
